/**
 * v1 `traffic_control` adapter (backfill).
 *
 * Payload shape (verified on staging 2026-05-15):
 *   { current_timestamp, drone_serial_number, track_origin_type, status }
 *
 * The numeric `status` field is the drone's operational state. The exact
 * code table isn't documented in v2 spec; observation says `status:1`
 * corresponds to a healthy aircraft (Active). For now we map:
 *
 *   1 → 'Active'
 *   2 → 'DAA'
 *   3 → 'DAA-DEG'
 *   4 → 'SA'
 *   5 → 'LOST'
 *
 * Any unknown code → null (the UI will fall back to `—`). The mapping
 * can be refined once Brayden confirms the v1 enum, but this gives us
 * a real value in the v2 status badge today instead of an empty `—`.
 */
import { z } from 'zod';
import type { TelemetryEnvelope } from '@/lib/mqtt/schemas/telemetry';

export const V1TrafficControlPayload = z.object({
  drone_serial_number: z.string(),
  status: z.number().int().nullish(),
  current_timestamp: z.string().nullish(),
  track_origin_type: z.string().nullish(),
});
export type V1TrafficControlPayload = z.infer<typeof V1TrafficControlPayload>;

const STATUS_MAP: Record<number, NonNullable<TelemetryEnvelope['status']>> = {
  1: 'Active',
  2: 'DAA',
  3: 'DAA-DEG',
  4: 'SA',
  5: 'LOST',
};

export interface TrafficControlUpdate {
  drone_id: string;
  status: TelemetryEnvelope['status'];
  status_label: string | null;
}

export function trafficControlToUpdate(raw: unknown): TrafficControlUpdate | null {
  const parsed = V1TrafficControlPayload.safeParse(raw);
  if (!parsed.success) return null;
  const v = parsed.data;
  const status = v.status != null ? STATUS_MAP[v.status] ?? null : null;
  return {
    drone_id: v.drone_serial_number,
    status,
    status_label: status,
  };
}
